/**
 * The staff search box — FR-B6, T-1.8.
 *
 * One field on every screen, **in the top bar, immediately left of the org
 * control** (owner, 2026-09-13). The bar is still suite chrome and this app
 * still composes nothing of its own *around* it: the field goes through the
 * `search` slot `@fiveox/shell` gained for exactly this — ADR-0037 said search
 * would get a slot once a service served it, and FR-B6 is that service.
 *
 * ## Two placements, one component
 *
 * * `bar` — from `lg` up. The shell hides the slot below `lg`, so this is a
 *   fixed-width field whose results drop down wider than it, aligned to its
 *   right edge so they never run off the viewport;
 * * `content` — below `lg` only, full width above the page, which is where the
 *   box lived before it moved. A phone or a tablet keeps search rather than
 *   losing it to a bar with no room: at 800px the bar field pushed the account
 *   menu off the screen, which is why this is `lg` and not `md`.
 *
 * Both are mounted and exactly one is displayed; `display: none` takes the
 * other out of the tab order, so a keyboard never meets two search fields.
 *
 * ## What it renders that a naive search box does not
 *
 * * **the kinds it could not search.** `meta.refused` is the caller's own
 *   permissions; an associate is told invoices are not theirs to search rather
 *   than being shown a result list that quietly lacks them. An empty answer
 *   that looks like an empty firm is the failure this avoids;
 * * **why a row matched**, when it was not the name. Somebody who pasted a PAN
 *   wants to see that the PAN is what matched, not a substring of a name;
 * * **"more behind this"** when a kind filled its limit, with the list screen a
 *   click away. Five per kind is a drop-down, not a report.
 *
 * ## It debounces, and it refuses to ask about one character
 *
 * `MIN_TERM` mirrors the service's, so the box does not fire a request per
 * keystroke that it knows will be refused. The debounce is 250ms — long enough
 * that typing a PAN is one request, short enough that it does not feel late.
 */

import { Badge, Card, CardBody, Input } from '@fiveox/ui'
import { Search } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { ApiError } from '../lib/api'
import {
  KIND_LABELS,
  type SearchMeta,
  type SearchResult,
  isSearchable,
  pathFor,
  resultSubtitle,
  search,
  searchMeta,
} from '../lib/api/search'

const DEBOUNCE_MS = 250

export type SearchPlacement = 'bar' | 'content'

/** Exported for `tests/search.test.ts`, which pins the breakpoint agreement. */
export const SEARCH_PLACEMENT = {
  bar: {
    box: 'relative w-80',
    size: 'sm',
    results: 'right-0 w-[28rem] max-w-[calc(100vw-2rem)]',
    placeholder: 'Search clients, people, cases…',
  },
  content: {
    // `lg:hidden` is ours; the bar's `hidden lg:block` is the shell's. The two
    // breakpoints must agree or a tablet gets two fields or none.
    box: 'relative mb-6 lg:hidden',
    size: 'md',
    results: 'w-full',
    placeholder: 'Search clients, people, cases, invoices and leads',
  },
} as const

export function SearchBox({ placement = 'content' }: { placement?: SearchPlacement }) {
  const look = SEARCH_PLACEMENT[placement]
  const navigate = useNavigate()
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [meta, setMeta] = useState<SearchMeta | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [highlight, setHighlight] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isSearchable(term)) {
      setResults([])
      setMeta(null)
      setError(null)
      return
    }
    // Cancelled on the next keystroke **and** on unmount: a late response that
    // arrives after the term changed would render answers to a question the
    // user has stopped asking.
    let cancelled = false
    const timer = window.setTimeout(() => {
      setBusy(true)
      search
        .run(term)
        .then((body) => {
          if (cancelled) return
          setResults(body.data.results)
          setMeta(searchMeta(body.meta))
          setHighlight(0)
          setOpen(true)
          setError(null)
        })
        .catch((err) => {
          if (cancelled) return
          setResults([])
          setError(err instanceof ApiError ? err.message : 'That search did not complete.')
          setOpen(true)
        })
        .finally(() => {
          if (!cancelled) setBusy(false)
        })
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [term])

  // Clicking away closes it. A dropdown that stays open over the page a person
  // has moved on to is the one interaction people complain about.
  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [open])

  const go = useCallback(
    (result: SearchResult) => {
      setOpen(false)
      setTerm('')
      navigate(pathFor(result))
    },
    [navigate],
  )

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!open || results.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlight((index) => (index + 1) % results.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight((index) => (index - 1 + results.length) % results.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const chosen = results[highlight]
      if (chosen) go(chosen)
    }
  }

  return (
    <div ref={boxRef} className={look.box} data-search-placement={placement}>
      <div className="relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onFocus={() => term && setOpen(true)}
          onKeyDown={onKeyDown}
          size={look.size}
          className="pl-9"
          placeholder={look.placeholder}
          aria-label="Search"
          autoComplete="off"
        />
      </div>

      {open && isSearchable(term) && (
        <Card className={`absolute z-30 mt-1 shadow-lg ${look.results}`}>
          <CardBody className="p-0">
            {error && <p className="p-3 text-sm text-destructive">{error}</p>}

            {!error && results.length === 0 && (
              <p className="p-3 text-sm text-muted-foreground">
                {busy ? 'Searching…' : `Nothing matches “${term}”.`}
              </p>
            )}

            {/* Bounded by the window as well as by 24rem (2026-09-25): the bar's
                results hang under a bar that does not scroll, so on a short
                window a fixed 24rem ran off the bottom with no way to reach the
                last rows. */}
            <ul className="max-h-[min(24rem,calc(100dvh-8rem))] overflow-y-auto">
              {results.map((result, index) => (
                <li key={`${result.kind}-${result.id}`}>
                  <button
                    type="button"
                    onClick={() => go(result)}
                    onMouseEnter={() => setHighlight(index)}
                    className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left ${
                      index === highlight ? 'bg-surface-raised' : ''
                    }`}
                  >
                    <span className="text-sm font-medium text-foreground">
                      {result.label ?? '—'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {resultSubtitle(result)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {/* The caller's own permissions, said out loud — see the module
                docstring. Without this an associate reads "no invoices" as a
                fact about the firm. */}
            {meta && meta.refused.length > 0 && (
              <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
                Not searched, because your role does not include{' '}
                {meta.refused.map((kind) => KIND_LABELS[kind].toLowerCase()).join(', ')}.
              </p>
            )}

            {meta?.limited && (
              <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
                <Badge tone="info">More</Badge> Only the first {meta.limit} of each
                kind are shown — open the list screen to see them all.
              </p>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  )
}
